import React, { useRef, useState } from 'react';

import { Box, Flex, Hide, Input, Show, Text } from '@chakra-ui/react';
import Check from 'icons/check.svg';
import Paperclip from 'icons/paperclip.svg';
import X from 'icons/x.svg';
import { FieldError, FieldErrorsImpl, UseFormSetValue, UseFormTrigger } from 'react-hook-form';

import { FormErrorMessage } from '@/components/atoms/FormErrorMessage/FormErrorMessage';
import { SUPPORTED_MIME_TYPES } from '@/utils/validation-schemas';

type FileUploadProps = {
  trigger: UseFormTrigger<any>;
  setValue: UseFormSetValue<any>;
  errors: Partial<FieldErrorsImpl>;
  loading?: boolean;
};

export const FileUpload = ({ trigger, setValue, errors, loading }: FileUploadProps) => {
  const fileUploadInputRef = useRef<HTMLInputElement>(null);
  const [filesToUpload, setFilesToUpload] = useState<File[] | null>(null);

  const onClickUpload = () => {
    if (fileUploadInputRef.current) {
      fileUploadInputRef.current.click();
    }
  };

  const onFilesChange = (files: FileList | null) => {
    if (files?.length) {
      const newFiles = Array.from(files);
      const filtered =
        filesToUpload?.filter((file) => {
          return !newFiles?.find((fl) => fl.name === file.name && fl.size === file.size);
        }) || [];
      const newList = [...filtered, ...newFiles];
      setFilesToUpload(newList);
      setValue('attachments', newList);
      trigger('attachments');
      if (fileUploadInputRef.current) {
        fileUploadInputRef.current.value = '';
      }
    }
  };

  const onFileRemove = (file: File) => {
    if (filesToUpload?.length) {
      const _files = [...filesToUpload];
      const filtered = _files.filter((fl) => fl.name !== file.name);
      setFilesToUpload(filtered);
      setValue('attachments', filtered);
      trigger('attachments');
    }
  };

  return (
    <>
      {!!filesToUpload?.length ? (
        <>
          {filesToUpload.map((file) => (
            <Flex justifyContent="space-between" key={file.name + file.size}>
              <Text
                color="lime.500"
                variant="text-md/lineHeight-6/font-medium"
                as="div"
                display="flex"
                alignItems="center"
                key={file.name + file.size}
                maxW={{
                  base: '90%',
                  xs: 'calc(100% - 220px)',
                }}
              >
                <Show above="xs">
                  <Box as={Check} color={'gray.700'} w="13px" h="9px" mr={2} minW="13px" />
                </Show>
                <Hide above="xs">
                  <Box as={X} w="18px" h="18px" mr={2} onClick={() => onFileRemove(file)} />
                </Hide>
                <Text
                  as="span"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  color="gray.700"
                >{`${file.name}`}</Text>
              </Text>
              <Show above="xs">
                <Text
                  as="div"
                  display="flex"
                  alignItems="center"
                  color="gray.700"
                  variant="text-md/lineHeight-6/font-medium"
                  cursor="pointer"
                  onClick={() => onFileRemove(file)}
                >
                  <Box as={X} w="18px" h="18px" mr={2} />
                  Remove attachment
                </Text>
              </Show>
            </Flex>
          ))}
        </>
      ) : null}
      <Flex
        justifyContent="center"
        alignItems="center"
        my={2}
        position="relative"
        borderRadius="8px"
        border="solid 1px #E0E0E0"
        bg="#FAFAFA"
        px="40px"
        py="16px"
      >
        {(filesToUpload?.length || 0) < 3 && (
          <Text
            as="div"
            display="flex"
            alignItems="center"
            color="purple.300"
            fontSize={{ base: '10px', md: '12px' }}
            lineHeight="16px"
            fontWeight="600"
            cursor="pointer"
            onClick={() => onClickUpload()}
          >
            <Box
              borderRadius="100%"
              border="solid 1px #C5C5C5"
              bg="#FFF"
              padding="3px"
              as={Paperclip}
              mr="8px"
              w={{ base: '24px', md: '32px' }}
              h={{ base: '24px', md: '32px' }}
            />
            <Flex flexDir="column">
              Deck, brief, etc.
              <Text fontSize={{ base: '10px', md: '12px' }} lineHeight="12px" fontWeight="600">
                PDF, DOCX
              </Text>
            </Flex>
          </Text>
        )}
        <FormErrorMessage errorMessage={errors.attachments as FieldError} left={0} />

        <Input
          variant="flushed"
          disabled={loading}
          aria-invalid={!!errors.attachments}
          multiple
          type="file"
          position="absolute"
          opacity="0"
          width="1px"
          height="1px"
          aria-hidden="true"
          accept={SUPPORTED_MIME_TYPES.join(',')}
          ref={fileUploadInputRef}
          onChange={(e) => onFilesChange(e.target.files)}
        />
      </Flex>
    </>
  );
};
